import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import Header from "../../../components/Header";
import { accountsmanagement } from "../../../config";
import { graphqlQuery, generateAndExecuteMutation } from "../../../data/Axios/DynamicService";
import { allAccountSubCategories, allTaxRates } from "../../../data/Axios/queries";

const base_url = accountsmanagement.uri;

const CreditNoteLinesForm = (props) => {
  const { isEditing = false, onClose, refetchData } = props; // Destructure props for clarity

  const [initialValues, setInitialValues] = useState({
    id: props.data ? props.data.id : null,
    creditNoteId: props.data ? props.data.creditNoteId : "",
    description: props.data ? props.data.description: "",
    accountSubCategoryId: props.data ? props.data.accountSubCategoryId : "",
    amount: props.data ? props.data.amount : 0,
    vatId: props.data ? props.data.vatId : "",
    vatAmount: props.data ? props.data.vatAmount: 0,
    isActive: props.data ? props.data.isActive : 1,
  });

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;
  const creditNoteData = JSON.parse(localStorage.getItem("MyCreditNoteData") || "{}");
  const creditNoteId = creditNoteData?.id || null;

  const [accountSubCategories, setAccountSubCategories] = useState([]);
  const [taxRates, setTaxRates] = useState([]);

  // Fetch master data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch account subcategories and filter for IDs 34 and 37
        const subCategoryData = await graphqlQuery(allAccountSubCategories, base_url);
        const filteredSubCategories = (subCategoryData || []).filter(
          (subCat) => [34, 37].includes(subCat.id)
        );
        setAccountSubCategories(filteredSubCategories);

        // Fetch tax rates and filter for taxTypeId = 1
        const taxRateData = await graphqlQuery(allTaxRates, base_url);
        const filteredTaxRates = (taxRateData || []).filter(
          (tax) => tax.taxTypeId === 1
        );
        setTaxRates(filteredTaxRates);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Map backend fields and set initial values
  useEffect(() => {
    if (props.data && props.isEditing) {
      const mappedData = {
        id: props.data.id || null,
        creditNoteId: props.data.creditNoteId || creditNoteId,
        description: props.data.description || "",
        accountSubCategoryId: props.data.accountSubCategoryId || "",
        amount: props.data.amount || 0,
        vatId: props.data.vatId || "",
        vatAmount: props.data.vatAmount || 0,
        isActive: props.data.isActive || 1,
      };
      setInitialValues(mappedData);
    } else {
      setInitialValues((prev) => ({
        ...prev,
        creditNoteId: creditNoteId,
      }));
    }
  }, [props.data, props.isEditing, creditNoteId]);

  // Validation schema
  const validationSchema = yup.object().shape({
    description: yup.string().required("Description is required"),
    accountSubCategoryId: yup.string().required("Account Subcategory is required"),
    amount: yup.number().min(0, "Amount must be positive").required("Amount is required"),
    vatId: yup.string().required("VAT is required"),
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Calculate VAT amount based on amount and selected VAT rate
 const calculateVatAmount = (amount, vatId) => {
  const selectedTax = taxRates.find(
    (tax) => tax.id === parseInt(vatId)
  );

  console.log("Amount:", amount);
  console.log("VAT Rate:", selectedTax?.rate);

  if (!selectedTax || !amount) return 0;

  const vat = parseFloat(amount) * (selectedTax.rate / 100);

  console.log("Calculated VAT:", vat);

  return vat.toFixed(2);
};

  // Submit handler
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const baseMutationData = {
        // Ensure all values are parsed correctly before payload assembly
        id: values.id || null, // Keep null for create operation
        creditNoteId: parseInt(creditNoteId),
        description: values.description,
        accountSubCategoryId: parseInt(values.accountSubCategoryId),
        amount: parseFloat(values.amount),
        vatId: parseInt(values.vatId),
        // Use the calculated VAT amount from the helper function
        vatAmount: parseFloat(calculateVatAmount(values.amount, values.vatId)),
        isActive: values.isActive,
      };

      let finalPayload;
      let mutationName;
      let mutationInputName; // The argument name expected by the server

      if (isEditing) {
        // Update mutation: expects a single object
        mutationName = "updateCreditNotesLine";
        // Argument name is typically singular for updates
        mutationInputName = "creditNotesLines"; 
        finalPayload = { ...baseMutationData, updatedBy: userId };
      } else {
        // Create mutation: expects an array of objects
        mutationName = "createCreditNotesLine";
        // Argument name is typically plural for batch creation
        mutationInputName = "creditNotesLines"; 
        
        // CRITICAL FIX: Wrap the payload in an array for creation
        finalPayload = [{ ...baseMutationData, createdBy: userId }]; 
      }

      // Execute the mutation with the corrected input argument name
      const response = await generateAndExecuteMutation(
        mutationName,
        finalPayload,
        mutationInputName, // Passes the correct argument name to the utility function
        base_url
      );

      if (response) {
        onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Credit Note Line ${isEditing ? "updated" : "created"} successfully.`,
        }).then(() => {
          refetchData();
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong while saving the Credit note line. Please check the console for server details.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Credit Note Line" : "Create Credit Note Line"}
        subtitle={isEditing ? "Edit existing Credit Note Line" : "Create new Credit Note Line"}
      />

      <Formik
        onSubmit={handleSubmit}
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="25px"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* Description */}
              <TextField
                fullWidth
                variant="filled"
                label="Description *"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
              />

              {/* Account Subcategory */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Account Subcategory *</InputLabel>
                <Select
                  name="accountSubCategoryId"
                  value={values.accountSubCategoryId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.accountSubCategoryId && !!errors.accountSubCategoryId}
                >
                  {accountSubCategories.map((subCat) => (
                    <MenuItem key={subCat.id} value={subCat.id}>
                      {subCat.accountCategoryName}
                    </MenuItem>
                  ))}
                </Select>
                {touched.accountSubCategoryId && errors.accountSubCategoryId && (
                  <Typography color="error">{errors.accountSubCategoryId}</Typography>
                )}
              </FormControl>

              {/* Amount */}
              <TextField
                fullWidth
                variant="filled"
                label="Amount *"
                name="amount"
                type="number"
                value={values.amount}
                onChange={(e) => {
                  handleChange(e);
                  // VAT amount calculation is triggered by both amount and VAT ID change
                  setFieldValue("vatAmount", calculateVatAmount(e.target.value, values.vatId));
                }}
                onBlur={handleBlur}
                error={touched.amount && !!errors.amount}
                helperText={touched.amount && errors.amount}
              />

              {/* VAT */}
              <FormControl fullWidth variant="filled">
                <InputLabel>VAT *</InputLabel>
                <Select
                  name="vatId"
                  value={values.vatId}
                  onChange={(e) => {
                    handleChange(e);
                    // VAT amount calculation is triggered by both amount and VAT ID change
                    setFieldValue("vatAmount", calculateVatAmount(values.amount, e.target.value));
                  }}
                  onBlur={handleBlur}
                  error={touched.vatId && !!errors.vatId}
                >
                  {taxRates.map((tax) => (
                    <MenuItem key={tax.id} value={tax.id}>
                      {tax.name} ({tax.rate}%)
                    </MenuItem>
                  ))}
                </Select>
                {touched.vatId && errors.vatId && (
                  <Typography color="error">{errors.vatId}</Typography>
                )}
              </FormControl>

              {/* VAT Amount (Read-only) */}
              <TextField
                fullWidth
                variant="filled"
                label="VAT Amount"
                name="vatAmount"
                type="number"
                // Recalculate VAT amount directly from the helper function for display
                value={calculateVatAmount(values.amount, values.vatId)}
                InputProps={{ readOnly: true }}
                disabled
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Submitting..."
                  : isEditing
                  ? "Update"
                  : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreditNoteLinesForm;